import { c as _c } from "react/compiler-runtime";
import { Button, Modal, Popover, Radio } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgKebab } from '@common/icon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { scroller } from 'react-scroll';
import { AuthMutation } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { SELECTOR } from '@/constants';
import { invalidateExceptCommentLists, patchCommentInAllLists, reconcileCommentListsAfterChange } from './_hooks/commentListSync';
import { useDeleteCommentMutation } from './_hooks/useDeleteCommentMutation';
export interface CommentMenuProps {
  isMyComment: boolean;
  commentId: number;
  onClickEditButton?: (commentId: number) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
}
const REPORT_REASONS = ['홍보성 광고글', '음란성, 선정성 글', '욕설', '개인정보 노출', '반복 게시글', '기타'];

// 신고 폼은 ResponsiveDialog 안에서, 삭제 확인은 Modal로 분리.
type View = 'report' | 'delete';
export const CommentMenu = t0 => {
  const $ = _c(49);
  const {
    isMyComment,
    commentId,
    onClickEditButton,
    onDeleteSuccess
  } = t0;
  const [view, setView] = useState(null);
  const [selectedReason, setSelectedReason] = useState();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setView(null);
      setSelectedReason(undefined);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const close = t1;
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = AuthMutation["post:/wello/v2/auth/wello-comment-report"]();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== queryClient || $[3] !== toast) {
    t3 = {
      ...t2,
      onSuccess: (_, t4) => {
        const {
          body
        } = t4;
        const reportedCommentId = body?.welloCommentId;
        if (reportedCommentId) {
          patchCommentInAllLists(queryClient, reportedCommentId, {
            reportYN: true
          });
          reconcileCommentListsAfterChange({
            queryClient,
            commentId: reportedCommentId,
            checkReflected: _temp
          });
          scroller.scrollTo(SELECTOR.COMMENT_PREFIX + reportedCommentId, {
            offset: -100
          });
        }
        invalidateExceptCommentLists(queryClient);
        close();
        toast({
          message: "\uB313\uAE00\uC774 \uC2E0\uACE0\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
        });
      }
    };
    $[2] = queryClient;
    $[3] = toast;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const reportMutation = useMutation(t3);
  let t4;
  if ($[5] !== commentId || $[6] !== onDeleteSuccess) {
    t4 = {
      onDeleteSuccess: () => onDeleteSuccess?.(commentId)
    };
    $[5] = commentId;
    $[6] = onDeleteSuccess;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const deleteMutation = useDeleteCommentMutation(t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Popover.Trigger className="flex size-26 items-center justify-center"><SvgKebab className="size-14 text-Gray-700" /></Popover.Trigger>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== commentId || $[10] !== isMyComment || $[11] !== onClickEditButton) {
    t6 = <Popover.Content className="flex flex-col px-0 py-12 font-400 text-Gray-900 typo-body3">{isMyComment ? <>{onClickEditButton ? <button type="button" className="w-118 px-16 py-8 text-left hover:bg-Gray-25" onClick={() => {
          setIsPopoverOpen(false);
          onClickEditButton(commentId);
        }}>수정</button> : null}<button type="button" className="w-118 px-16 py-8 text-left hover:bg-Gray-25" onClick={() => {
          setIsPopoverOpen(false);
          setView("delete");
        }}>삭제</button></> : <button type="button" className="w-118 px-16 py-8 text-left hover:bg-Gray-25" onClick={() => {
        setIsPopoverOpen(false);
        setView("report");
      }}>신고</button>}</Popover.Content>;
    $[9] = commentId;
    $[10] = isMyComment;
    $[11] = onClickEditButton;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isPopoverOpen || $[14] !== t6) {
    t7 = <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen} floatingPadding={8} placement="bottom-end">{t5}{t6}</Popover.Root>;
    $[13] = isPopoverOpen;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = view === "report";
  let t9;
  if ($[16] !== commentId || $[17] !== reportMutation || $[18] !== selectedReason) {
    t9 = e => {
      e.preventDefault();
      if (!selectedReason) {
        return;
      }
      reportMutation.mutate({
        body: {
          welloCommentId: commentId,
          contents: selectedReason
        }
      });
    };
    $[16] = commentId;
    $[17] = reportMutation;
    $[18] = selectedReason;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2 className="font-700 text-Gray-900 typo-body1">신고하는 이유를 선택해주세요.</h2>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = REPORT_REASONS.map(_temp2);
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== selectedReason) {
    t12 = <Radio.Group className="flex-col gap-20" value={selectedReason} onChange={setSelectedReason}>{t11}</Radio.Group>;
    $[22] = selectedReason;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = !selectedReason;
  let t14;
  if ($[24] !== reportMutation.isPending || $[25] !== t13) {
    t14 = <Button className="w-full" disabled={t13} loading={reportMutation.isPending} type="submit">선택 완료</Button>;
    $[24] = reportMutation.isPending;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14 || $[29] !== t9) {
    t15 = <form className="flex flex-col gap-24" onSubmit={t9}>{t10}{t12}{t14}</form>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15 || $[32] !== t8) {
    t16 = <ResponsiveDialog opened={t8} onClose={close} drawerType="bottom">{t15}</ResponsiveDialog>;
    $[31] = t15;
    $[32] = t8;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = view === "delete";
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex flex-col gap-8"><p className="font-700 text-Gray-900 typo-body2">댓글 삭제</p><p className="font-400 text-Gray-700 typo-body3">삭제한 댓글은 복구할 수 없어요.</p></div>;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Button className="flex-1" dimensions="H48" theme="sub-fill" onClick={close}>취소</Button>;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== commentId || $[37] !== deleteMutation) {
    t20 = () => {
      deleteMutation.mutate({
        path: {
          welloCommentId: commentId
        }
      }, {
        onSettled: close
      });
    };
    $[36] = commentId;
    $[37] = deleteMutation;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== deleteMutation.isPending || $[40] !== t20) {
    t21 = <div className="flex flex-col gap-24">{t18}<div className="flex items-center gap-8">{t19}<Button className="flex-1" dimensions="H48" loading={deleteMutation.isPending} theme="primary-fill" onClick={t20}>삭제</Button></div></div>;
    $[39] = deleteMutation.isPending;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t17 || $[43] !== t21) {
    t22 = <Modal opened={t17} onClose={close}>{t21}</Modal>;
    $[42] = t17;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t16 || $[46] !== t22 || $[47] !== t7) {
    t23 = <>{t7}{t16}{t22}</>;
    $[45] = t16;
    $[46] = t22;
    $[47] = t7;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};
function _temp(comment) {
  return !comment || comment.reportYN;
}
function _temp2(reason) {
  return <Radio.Item key={reason} className="justify-start typo-body3" value={reason}>{reason}</Radio.Item>;
}